import { useEffect, useRef, useState } from "react";
import { motion, useInView, animate } from "framer-motion";
import { useLanguage } from "@/i18n";

const Counter = ({ value, decimals = 0, prefix = "", suffix = "", tid }) => {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-80px" });
  const [display, setDisplay] = useState("0");

  useEffect(() => {
    if (!inView) return;
    const controls = animate(0, value, {
      duration: 2.2,
      ease: [0.16, 1, 0.3, 1],
      onUpdate: (v) => setDisplay(v.toFixed(decimals)),
    });
    return () => controls.stop();
  }, [inView, value, decimals]);

  return (
    <span ref={ref} className="tabular-nums" data-testid={tid}>
      {prefix}{display}{suffix}
    </span>
  );
};

const Numbers = () => {
  const { t } = useLanguage();
  const stats = [
    { value: 80, prefix: "−", suffix: "%", label: t.numbers.d1, tid: "stat-times", accent: "text-thermal" },
    { value: 100, suffix: "%", label: t.numbers.d2, tid: "stat-standardization", accent: "text-white" },
    { value: 40, prefix: "+", suffix: "%", label: t.numbers.d3, tid: "stat-energy", accent: "text-thermal" },
    { value: 0.5, decimals: 1, prefix: "±", suffix: "°C", label: t.numbers.d4, tid: "stat-precision", accent: "text-micro" },
  ];

  return (
    <section id="numeri" className="border-b border-white/10" data-testid="numbers-section">
      <div className="px-5 md:px-10 pt-24 md:pt-36 pb-16">
        <motion.p
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="font-mono text-[10px] md:text-xs tracking-[0.35em] text-thermal mb-6"
          data-testid="numbers-label"
        >
          {t.numbers.label}
        </motion.p>
        <motion.h2
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
          className="font-display font-semibold text-4xl sm:text-5xl lg:text-6xl mb-16 md:mb-24"
          data-testid="numbers-title"
        >
          {t.numbers.title}
        </motion.h2>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 border-t border-l border-white/10">
          {stats.map((s, i) => (
            <motion.div
              key={s.tid}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.7, delay: i * 0.12, ease: [0.16, 1, 0.3, 1] }}
              className="border-b border-r border-white/10 p-8 md:p-10 hover:bg-white/[0.03] transition-colors duration-300"
              data-testid={`${s.tid}-cell`}
            >
              <div className={`font-mono font-bold text-5xl md:text-6xl lg:text-7xl mb-6 ${s.accent}`}>
                <Counter value={s.value} decimals={s.decimals || 0} prefix={s.prefix} suffix={s.suffix} tid={s.tid} />
              </div>
              <p className="font-mono text-[10px] md:text-xs tracking-[0.25em] text-white/50 uppercase">{s.label}</p>
            </motion.div>
          ))}
        </div>

        <p className="font-mono text-[10px] text-white/30 tracking-widest mt-6" data-testid="numbers-note">
          * {t.numbers.note}
        </p>
      </div>
    </section>
  );
};

export default Numbers;
